import { Spinner } from '@delmonicos/shared-design-system-react/src/components/spinner/spinner';
import {
  getDecodedJwt,
  isValidJwt,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/is-valid-jwt';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import {
  authenticate,
  isAuthenticated,
} from '@app/core/authentication/authentication.slice';
import { useAppDispatch, useAppSelector } from '@app/core/hooks';
import { Logger } from '@app/core/logger/logger';
import { InAppStorage } from '@app/core/storage/in-app-storage';
import { useCpmsBoRefreshTokensMutation } from '@service/auth.service';

export function AuthenticatedController(): React.JSX.Element {
  const isJwtValid = useAppSelector(isAuthenticated);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(isJwtValid);
  const [submitRefreshToken, { isLoading: isRefreshing }] =
    useCpmsBoRefreshTokensMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoading = useRef(false);

  useEffect(() => {
    if (isLoading.current) return;
    isLoading.current = true;
    const handleRefreshToken = async () => {
      if (!isUserAuthenticated && !isRefreshing) {
        const { refreshToken } = InAppStorage;
        if (refreshToken) {
          const { refreshToken: refreshTokenValue, wasRemembered } =
            refreshToken;
          const decodedRefreshToken = refreshTokenValue
            ? getDecodedJwt(refreshTokenValue)
            : null;

          if (
            decodedRefreshToken === null ||
            !isValidJwt(decodedRefreshToken)
          ) {
            throw new Error('refreshToken has expired or is null');
          }
          await submitRefreshToken({ refreshToken: refreshTokenValue })
            .then((result) => {
              if (!result.data) {
                throw new Error('refresh token failed');
              } else {
                InAppStorage.setJwt(result.data.accessToken, wasRemembered);
                InAppStorage.setRefreshToken(
                  result.data.refreshToken,
                  wasRemembered,
                );
                dispatch(authenticate(result.data.accessToken));
              }
            })
            .catch(() => {
              throw new Error('refresh token failed');
            });
          return;
        }

        throw new Error('refreshToken does not exist');
      }
    };

    handleRefreshToken()
      .then(() => {
        setIsUserAuthenticated(true);
        isLoading.current = false;
      })
      .catch((err) => {
        Logger.error(err);
        setIsUserAuthenticated(false);
        isLoading.current = false;
        navigate(APP_PATH.LOGIN, { state: { from: location } });
      });
  }, [
    dispatch,
    isRefreshing,
    isUserAuthenticated,
    location,
    navigate,
    submitRefreshToken,
  ]);

  if (!isUserAuthenticated) {
    return <Spinner />;
  }

  return <Outlet />;
}
